import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Button,
  Grow,
  Autocomplete,
  Select,
  MenuItem,
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Sync as SyncIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import AdvancedPagination from '@/components/pagination/AdvancedPagination'
import { useDebounce } from '@/hooks/useDebounce'
import {
  getEVVLogs,
  exportEVVLogsCSV,
  EVVLog,
  EVVLogParams,
  getContractDropdowns,
  ContractDropdownOption,
} from '@/services/evvLogsService'
import ClockInDialog from '@/components/contracts/ClockInDialog'
import EvvLogViewPopup from '@/components/EvvLogViewPopup'
import {
  useAgencyDirectory,
  getDirectoryOptionLabel,
} from '@/hooks/useAgencyDirectory'
import { useAuth } from '@/hooks/useAuth'

const AgencyEVVLogsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const [evvLogs, setEvvLogs] = useState<EVVLog[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [totalRecords, setTotalRecords] = useState<number>(0)

  // Filters
  const [search, setSearch] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [contractId, setContractId] = useState<string>('')
  const [filterPatientId, setFilterPatientId] = useState<string>('')
  const [filterProviderId, setFilterProviderId] = useState<string>('')

  const { patients, providers } = useAgencyDirectory()

  // Dropdown options
  const [contractOptions, setContractOptions] = useState<
    ContractDropdownOption[]
  >([])
  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [selectedContract, setSelectedContract] =
    useState<ContractDropdownOption | null>(null)

  // Pagination
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)

  const debouncedSearch = useDebounce(search, 500)
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false)
  const [logData, setLogData] = useState<EVVLog | null>(null)
  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoadingDropdowns(true)
      try {
        const contracts = await getContractDropdowns()
        setContractOptions(contracts)
      } catch (err) {
        console.error('Error fetching contract dropdowns:', err)
      } finally {
        setLoadingDropdowns(false)
      }
    }
    fetchDropdowns()
  }, [])

  const fetchEVVLogs = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Convert dates to YYYY-MM-DD format for API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined
        return format(date, 'yyyy-MM-dd')
      }

      const params: EVVLogParams = {
        search: debouncedSearch || undefined,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        contractId: contractId || undefined,
        patientId: filterPatientId || undefined,
        therapistId: filterProviderId || undefined,
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      }

      const response = await getEVVLogs(params)
      setEvvLogs(response.evvlog || [])
      setTotalRecords(response.totalRecords || 0)
    } catch (err: any) {
      console.error('Error fetching EVV logs:', err)
      setError(err?.response?.data?.message || 'Failed to fetch EVV logs')
      setEvvLogs([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }, [
    debouncedSearch,
    startDate,
    endDate,
    contractId,
    filterPatientId,
    filterProviderId,
    page,
    rowsPerPage,
  ])

  useEffect(() => {
    fetchEVVLogs()
  }, [fetchEVVLogs])

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleExportCSV = async () => {
    try {
      setLoading(true)

      // Convert dates to YYYY-MM-DD format for API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined
        return format(date, 'yyyy-MM-dd')
      }

      const params: Omit<EVVLogParams, 'page' | 'limit'> = {
        search: debouncedSearch || undefined,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        contractId: contractId || undefined,
        patientId: filterPatientId || undefined,
        therapistId: filterProviderId || undefined,
      }

      const blob = await exportEVVLogsCSV(params)

      // Create download link
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `evv-logs-${format(new Date(), 'yyyy-MM-dd')}.xlsx`
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Error exporting EVV logs:', err)
      setError(err?.response?.data?.message || 'Failed to export EVV logs')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setSearch('')
    setStartDate(null)
    setEndDate(null)
    setContractId('')
    setSelectedContract(null)
    setFilterPatientId('')
    setFilterProviderId('')
    setPage(0)
  }

  const hasActiveFilters = Boolean(
    search ||
    startDate ||
    endDate ||
    contractId ||
    filterPatientId ||
    filterProviderId
  )

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy hh:mm a')
    } catch {
      return dateString
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'hh:mm a')
    } catch {
      return dateString
    }
  }

  const formatDuration = (
    duration?: number,
    clockIn?: string,
    clockOut?: string
  ): string => {
    // If duration is provided from API, use it
    if (duration !== undefined && duration !== null) {
      const hours = Math.floor(duration / 60)
      const minutes = duration % 60
      if (hours > 0) {
        return `${hours}h ${minutes}m`
      }
      return `${minutes}m`
    }

    // Otherwise calculate from clock in/out times
    if (clockIn && clockOut) {
      try {
        const start = new Date(clockIn)
        const end = new Date(clockOut)
        const diffMs = end.getTime() - start.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const hours = Math.floor(diffMins / 60)
        const minutes = diffMins % 60
        if (hours > 0) {
          return `${hours}h ${minutes}m`
        }
        return `${minutes}m`
      } catch {
        return 'N/A'
      }
    }

    return 'N/A'
  }

  const getContractId = (log: EVVLog): string => {
    if (typeof log.contract_id === 'object' && log.contract_id?.contract_ids) {
      return log.contract_id.contract_ids
    }
    if (typeof log.contract_id === 'string') {
      return log.contract_id
    }
    return 'N/A'
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: 'primary.main', fontWeight: 600 }}
            gutterBottom
          >
            EVV Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage Electronic Visit Verification logs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            disabled={loading || evvLogs.length === 0}
            sx={{
              textTransform: 'none',
              borderRadius: '0.5rem',
              fontWeight: 500,
            }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filters Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '1rem',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-end',
              }}
            >
              {/* Search Filter */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(40% - 8px)',
                    lg: '2 1 300px',
                  },
                  minWidth: { xs: '100%', sm: '200px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Search
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by patient or session ID..."
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: 'text.secondary', fontSize: '18px' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& fieldset': {
                        borderColor: 'grey.300',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Date Filters Group */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(30% - 8px)',
                    lg: '1 1 280px',
                  },
                  minWidth: { xs: '100%', sm: '280px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Start date & End date
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue: Date | null) => setStartDate(newValue)}
                    format="MM/dd/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        placeholder: 'MM/DD/YYYY',
                        sx: {
                          flex: 1,
                          minWidth: '120px',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            minWidth: '100px',
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    value={endDate}
                    onChange={(newValue: Date | null) => setEndDate(newValue)}
                    format="MM/dd/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        placeholder: 'MM/DD/YYYY',
                        sx: {
                          flex: 1,
                          minWidth: '120px',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            minWidth: '100px',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Contract ID Filter */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(30% - 8px)',
                    lg: '1 1 180px',
                  },
                  minWidth: { xs: '100%', sm: '180px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Contract ID
                </Typography>
                <Autocomplete
                  options={contractOptions}
                  getOptionLabel={option => option.contract_ids || ''}
                  value={selectedContract}
                  onChange={(event, newValue) => {
                    setSelectedContract(newValue)
                    setContractId(newValue?._id || '')
                  }}
                  loading={loadingDropdowns}
                  size="small"
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Select Contract ID"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start" sx={{ mr: 0.5 }}>
                              <PersonIcon
                                fontSize="small"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '18px',
                                }}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0.625rem',
                          backgroundColor: 'grey.50',
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: 'grey.100',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'background.paper',
                          },
                          '& fieldset': {
                            borderColor: 'grey.300',
                          },
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1.5px',
                          },
                        },
                      }}
                    />
                  )}
                  slotProps={{
                    popper: { sx: { zIndex: 9999 } },
                  }}
                />
              </Box>

              {/* Patient Filter */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(30% - 8px)',
                    lg: '1 1 180px',
                  },
                  minWidth: { xs: '100%', sm: '180px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Patient
                </Typography>
                <Select
                  value={filterPatientId}
                  onChange={event => setFilterPatientId(event.target.value as string)}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{
                    borderRadius: '0.625rem',
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'background.paper',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.300',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '1.5px',
                    },
                  }}
                >
                  <MenuItem value="">All Patients</MenuItem>
                  {patients.map(patient => (
                    <MenuItem key={patient._id} value={patient._id}>
                      {getDirectoryOptionLabel(patient)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Provider Filter */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(30% - 8px)',
                    lg: '1 1 180px',
                  },
                  minWidth: { xs: '100%', sm: '180px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Provider
                </Typography>
                <Select
                  value={filterProviderId}
                  onChange={event => setFilterProviderId(event.target.value as string)}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{
                    borderRadius: '0.625rem',
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'background.paper',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.300',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '1.5px',
                    },
                  }}
                >
                  <MenuItem value="">All Providers</MenuItem>
                  {providers.map(provider => (
                    <MenuItem key={provider._id} value={provider._id}>
                      {getDirectoryOptionLabel(provider)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Reset Filter Button */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '0 0 auto',
                  },
                  minWidth: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon sx={{ fontSize: '18px' }} />}
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '0.625rem',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    px: 2.5,
                    py: 1,
                    height: '40px',
                    minWidth: '100px',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table Card */}
      <Grow in timeout={300}>
        <Card sx={{ boxShadow: 2 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 400,
                }}
              >
                <CircularProgress />
              </Box>
            ) : evvLogs.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 400,
                  gap: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No EVV logs found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {hasActiveFilters
                    ? 'Try adjusting or clearing your filters to see more results.'
                    : 'EVV logs will appear here once sessions are clocked in.'}
                </Typography>
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Contract ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Session ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Clock In</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Clock Out</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evvLogs.map((log, index) => {
                      // Extract date from clock_in or createdAt
                      const logDate = log.clock_in || log.createdAt
                      const contractId = getContractId(log)
                      const duration = formatDuration(
                        log.duration,
                        log.clock_in,
                        log.clock_out
                      )

                      return (
                        <Grow
                          in
                          key={log._id}
                          timeout={200 + index * 50}
                          style={{ transformOrigin: '0 0 0' }}
                        >
                          <TableRow
                            hover
                            sx={{
                              cursor: 'default',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2">
                                {logDate ? formatDate(logDate) : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {contractId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {log.session_ids || log.session_id || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {log.patient || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 300,
                                }}
                              >
                                {log.address || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {log.clock_in
                                  ? formatTime(log.clock_in)
                                  : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {log.clock_out
                                  ? formatTime(log.clock_out)
                                  : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {duration}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => { setClockInDialogOpen(true), setLogData(log) }}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: '0.625rem',
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  fontSize: '0.8125rem',
                                  px: 2.5,
                                  py: 1,
                                  height: '40px',
                                  minWidth: '100px',
                                  '&:hover': {
                                    borderColor: 'primary.dark',
                                    backgroundColor: 'action.hover',
                                  },
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        </Grow>
                      )
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                  <AdvancedPagination
                    count={totalRecords}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </Box>
              </>
            )}
          </TableContainer>
        </Card>
      </Grow>
      {logData && (
        <EvvLogViewPopup
          open={clockInDialogOpen}
          onClose={() => setClockInDialogOpen(false)}
          logData={logData}

        />
      )}
    </Box>
  )
}

export default AgencyEVVLogsPage
